export function dashboardStore() {
  return {
    messages: [],
    input: '',
    lastId: 0,
    userId: window.dcUserId,
    timer: null,
    clockTimer: null,
    realtimeSource: null,
    realtimeConnected: false,
    realtimeInitialized: false,
    realtimeStarting: false,
    realtimeLastReconnectAt: 0,
    reloadWaitTime: 10000,
    clockWaitTime: 60000,
    realtimeFallbackWaitTime: 60000,
    timeString: '',
    dateString: '',
    dashboardView: 'notifications',
    canViewCharts: false,
    dashboardApp: null,
    messageSignature: '',
    visibilityHandler: null,
    isRefreshingCharts: false,

    reminderWaitTime: 60000,
    reminderTimer: null,
    repeatEnabled: false,
    lastNotificationAt: 0,
    pendingRealtimeNotification: false,

    audio: null,
    audioUnlocked: false,
    audioEnabled: false,
    audioVolume: 100,
    lastVolumePreviewAt: 0,

    getCurrentShopId() {
      return Number(window.dcDashboardConfig?.shopId || 0);
    },

    init() {
      this.dashboardApp = document.getElementById('dashboardApp');
      const isDashboard = this.dashboardApp !== null;
      if (!isDashboard) return;

      this.setupAudioUnlock();
      this.canViewCharts = Boolean(
        window.dcDashboardConfig?.canViewCharts
      );
      this.updateDateTime();
      this.clockTimer = setInterval(() => {
        this.updateDateTime();
        this.updateMessageAges();
      }, this.clockWaitTime);

      this.fetchMessages({ playSound: false });
      this.startRealtimeNotifications();
      this.startReminderTimer();
      if (!this.realtimeSource) {
        this.startMessagePolling();
      }

      this.$watch('dashboardView', (value) => {
        if (value === 'notifications') {
          this.fetchMessageChanges({ playSound: false });
          if (!this.realtimeConnected) {
            this.startMessagePolling();
          }
          return;
        }

        if (this.realtimeConnected) {
          this.stopMessagePolling();
        }
      });

      this.visibilityHandler = () => {
        if (document.hidden) {
          this.stopMessagePolling();
          return;
        }

        if (this.dashboardView === 'notifications') {
          this.fetchMessageChanges({ playSound: false });
          if (!this.realtimeConnected) {
            this.startMessagePolling();
          }
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    },

    destroy() {
      this.stopMessagePolling();
      this.stopRealtimeNotifications();
      this.stopReminderTimer();

      if (this.clockTimer) {
        clearInterval(this.clockTimer);
        this.clockTimer = null;
      }

      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
    },

    startReminderTimer() {
      if (this.reminderTimer) {
        return;
      }

      this.reminderTimer = setInterval(() => {
        this.playPendingReminder();
      }, this.reminderWaitTime);
    },

    stopReminderTimer() {
      if (!this.reminderTimer) {
        return;
      }

      clearInterval(this.reminderTimer);
      this.reminderTimer = null;
    },

    toggleRepeatNotifications() {
      this.repeatEnabled = !this.repeatEnabled;
      window.localStorage?.setItem(
        'qrshop-dashboard-repeat-notifications',
        this.repeatEnabled ? 'on' : 'off'
      );

      if (this.repeatEnabled) {
        this.lastNotificationAt = Date.now();
      }
    },

    hasPendingMessages() {
      return (
        this.pendingRealtimeNotification ||
        this.hasPendingMessageList(this.messages)
      );
    },

    hasPendingMessageList(list) {
      if (!Array.isArray(list)) {
        return false;
      }

      return list.some((item) => {
        const type = String(item?.type || '');
        return type && !type.includes('accepted');
      });
    },

    playPendingReminder() {
      if (
        !this.repeatEnabled ||
        !this.audioEnabled ||
        !this.hasPendingMessages()
      ) {
        return;
      }

      const now = Date.now();

      if (now - this.lastNotificationAt < this.reminderWaitTime) {
        return;
      }

      this.playNotificationSound();
    },

    toggleDashboardView() {
      if (!this.canViewCharts) {
        this.dashboardView = 'notifications';
        return;
      }

      this.dashboardView =
        this.dashboardView === 'notifications'
          ? 'consultations'
          : 'notifications';
    },

    startMessagePolling(waitTime = this.reloadWaitTime) {
      if (
        this.timer ||
        this.dashboardView !== 'notifications' ||
        document.hidden
      ) {
        return;
      }

      this.timer = setInterval(() => {
        this.updateMessageAges();
        this.fetchMessageChanges();
      }, waitTime);
    },

    stopMessagePolling() {
      if (!this.timer) {
        return;
      }

      clearInterval(this.timer);
      this.timer = null;
    },

    getRealtimeConfig() {
      const localized = window.frontendAjaxObject?.firebase || {};
      const dashboard = window.dcDashboardConfig || {};
      const databaseURL = String(localized.databaseURL || '').replace(/\/$/, '');
      const appType = String(dashboard.appType || localized.appType || 'salon');
      const shopId = Number(dashboard.shopId || 0);
      const eventDate = String(dashboard.eventDate || this.getLocalEventDate());

      if (
        !databaseURL ||
        !appType ||
        !shopId ||
        !/^\d{8}$/.test(eventDate) ||
        typeof EventSource === 'undefined'
      ) {
        return null;
      }

      return { databaseURL, appType, shopId, eventDate };
    },

    getLocalEventDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');

      return `${year}${month}${day}`;
    },

    getRealtimeStreamUrl(authToken) {
      const config = this.getRealtimeConfig();

      if (!config) {
        return '';
      }

      const path = [
        'apps',
        encodeURIComponent(config.appType),
        'shops',
        encodeURIComponent(config.shopId),
        'events',
        `${encodeURIComponent(config.eventDate)}.json`,
      ].join('/');
      const params = new URLSearchParams({
        orderBy: '"$key"',
        limitToLast: '1',
      });

      if (authToken) {
        params.set('auth', authToken);
      }

      return `${config.databaseURL}/${path}?${params.toString()}`;
    },

    // RTDB events 규칙을 auth != null 로 조이기 위한 익명 인증 토큰.
    // Firebase ESM SDK 를 필요할 때만 동적으로 불러온다(대시보드 페이지에는
    // SDK 가 없다). 실패하면 빈 문자열 — 무인증 SSE 로 폴백(현행 규칙 호환).
    async getRealtimeAuthToken() {
      const fb = window.frontendAjaxObject?.firebase || {};

      if (!fb.apiKey || !fb.databaseURL) {
        return '';
      }

      try {
        const base = 'https://www.gstatic.com/firebasejs/11.10.0';
        const [appMod, authMod] = await Promise.all([
          import(/* @vite-ignore */ `${base}/firebase-app.js`),
          import(/* @vite-ignore */ `${base}/firebase-auth.js`),
        ]);
        const app = appMod.getApps().length
          ? appMod.getApp()
          : appMod.initializeApp({
              apiKey: fb.apiKey,
              appId: fb.appId,
              projectId: fb.projectId,
              authDomain: fb.authDomain,
              databaseURL: fb.databaseURL,
            });
        const auth = authMod.getAuth(app);

        if (!auth.currentUser) {
          await authMod.signInAnonymously(auth);
        }

        return (await auth.currentUser?.getIdToken()) || '';
      } catch (e) {
        return '';
      }
    },

    async startRealtimeNotifications() {
      if (this.realtimeSource || this.realtimeStarting) {
        return;
      }

      this.realtimeStarting = true;
      let url = '';

      try {
        url = this.getRealtimeStreamUrl(await this.getRealtimeAuthToken());
      } finally {
        this.realtimeStarting = false;
      }

      if (!url || this.realtimeSource) {
        return;
      }

      this.realtimeInitialized = false;
      this.realtimeSource = new EventSource(url);

      this.realtimeSource.onopen = () => {
        this.realtimeConnected = true;
        this.stopMessagePolling();
      };

      this.realtimeSource.onerror = () => {
        this.realtimeConnected = false;
        this.startMessagePolling(this.realtimeFallbackWaitTime);
      };

      const handleRealtimeEvent = (event) => {
        const data = this.parseRealtimeMessage(event.data);

        if (!this.realtimeInitialized) {
          this.realtimeInitialized = true;
          return;
        }

        if (!data) {
          return;
        }

        this.refreshFromRealtimeEvent(data);
      };

      this.realtimeSource.onmessage = handleRealtimeEvent;
      this.realtimeSource.addEventListener('put', handleRealtimeEvent);
      this.realtimeSource.addEventListener('patch', handleRealtimeEvent);
      // 인증 토큰 만료(약 1시간)·권한 회수 시 서버가 스트림을 닫는다 —
      // 새 토큰으로 재연결한다. 30초 스로틀로 연쇄 재연결을 막고,
      // 실패하면 onerror 가 폴링 폴백을 켠다.
      const reconnect = () => {
        const now = Date.now();

        if (now - this.realtimeLastReconnectAt < 30000) {
          return;
        }

        this.realtimeLastReconnectAt = now;
        this.stopRealtimeNotifications();
        this.startRealtimeNotifications();
      };
      this.realtimeSource.addEventListener('auth_revoked', reconnect);
      this.realtimeSource.addEventListener('cancel', reconnect);
    },

    stopRealtimeNotifications() {
      if (!this.realtimeSource) {
        return;
      }

      this.realtimeSource.close();
      this.realtimeSource = null;
      this.realtimeConnected = false;
      this.realtimeInitialized = false;
    },

    parseRealtimeMessage(raw) {
      let message = null;

      try {
        message = JSON.parse(raw);
      } catch (e) {
        return null;
      }

      if (!message || message.data === null || message.data === undefined) {
        return null;
      }

      if (message.data.type) {
        return message.data;
      }

      if (typeof message.data === 'object') {
        return Object.values(message.data).find((item) => item?.type) || null;
      }

      return null;
    },

    async refreshFromRealtimeEvent(eventData) {
      const type = String(eventData?.type || '');

      if (type === 'consultation') {
        await this.refreshCustomerCharts();
        this.playNotificationSound();
        return;
      }

      if (
        type === 'cart' ||
        type === 'call' ||
        type === 'cart_accepted' ||
        type === 'call_accepted'
      ) {
        if (type === 'cart' || type === 'call') {
          this.pendingRealtimeNotification = true;
        }

        if (this.dashboardView === 'notifications' && !document.hidden) {
          await this.fetchMessageChanges({ playSound: false });
        }

        if (type === 'cart' || type === 'call') {
          this.playNotificationSound();
        }
      }
    },

    updateDateTime() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0'); // 0부터 시작해서 +1
      const date = String(now.getDate()).padStart(2, '0');

      const dateString = `${month}-${date}`;
      this.dateString = dateString;

      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const timeString = `${hours}:${minutes}`;
      this.timeString = timeString;
      const dateEl = document.getElementById('realtime-date');
      const timeEl = document.getElementById('realtime-clock');

      if (dateEl) dateEl.textContent = this.dateString;
      if (timeEl) timeEl.textContent = this.timeString;
    },

    setupAudioUnlock() {
      // 공용으로 쓸 Audio 인스턴스 준비
      this.audio = new Audio(
        `${window.frontendAjaxObject.themeurl}/assets/build/audio/call-1.wav`
      );
      this.audio.preload = 'auto';
      this.audioEnabled = false;
      this.audioUnlocked = false;
      this.repeatEnabled =
        window.localStorage?.getItem('qrshop-dashboard-repeat-notifications') ===
        'on';
      this.audioVolume = this.getStoredAudioVolume();
      this.applyAudioVolume();

      const unlock = () => {
        if (this.audioUnlocked || !this.audio) return;

        this.audio
          .play()
          .then(() => {
            // 한 번 재생해서 권한만 얻고 바로 멈춘다
            this.audio.pause();
            this.audio.currentTime = 0;
            this.audioUnlocked = true;

            // 더 이상 필요 없으니 이벤트 제거
            window.removeEventListener('click', unlock);
            window.removeEventListener('touchstart', unlock);
          })
          .catch((e) => {
            console.log('Audio unlock failed:', e);
          });
      };

      this.unlockAudio = unlock;
    },

    toggleAudioNotifications() {
      this.audioEnabled = !this.audioEnabled;

      if (this.audioEnabled) {
        this.unlockAudio?.();
        return;
      }

      this.audioUnlocked = false;
      if (this.audio) {
        this.audio.pause();
        this.audio.currentTime = 0;
      }
    },

    getStoredAudioVolume() {
      const stored = Number(
        window.localStorage?.getItem('qrshop-dashboard-sound-volume')
      );

      if (!Number.isFinite(stored)) {
        return 100;
      }

      return this.normalizeAudioVolume(stored);
    },

    normalizeAudioVolume(value) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return 100;
      }

      const stepped = Math.round(number / 5) * 5;
      return Math.min(100, Math.max(5, stepped));
    },

    setAudioVolume(value) {
      this.audioVolume = this.normalizeAudioVolume(value);
      window.localStorage?.setItem(
        'qrshop-dashboard-sound-volume',
        String(this.audioVolume)
      );
      this.applyAudioVolume();
    },

    applyAudioVolume() {
      if (!this.audio) {
        return;
      }

      this.audio.volume = this.audioVolume / 100;
    },

    previewNotificationVolume() {
      if (!this.audio || !this.audioEnabled) {
        return;
      }

      const now = Date.now();
      if (now - this.lastVolumePreviewAt < 250) {
        return;
      }
      this.lastVolumePreviewAt = now;

      this.applyAudioVolume();
      this.audio.currentTime = 0;
      this.audio
        .play()
        .then(() => {
          this.audioUnlocked = true;
        })
        .catch((e) => console.log('Audio preview failed:', e));
    },

    playNotificationSound() {
      if (!this.audio || !this.audioEnabled) {
        return;
      }

      this.applyAudioVolume();
      this.audio.currentTime = 0;
      this.audio
        .play()
        .then(() => {
          this.audioUnlocked = true;
          this.lastNotificationAt = Date.now();
        })
        .catch((e) => console.log('Audio play failed:', e));
    },

    async refreshCustomerCharts() {
      if (this.isRefreshingCharts) {
        return;
      }

      const content = this.$refs.consultationContent;
      if (!content) {
        return;
      }

      this.isRefreshingCharts = true;

      try {
        const res = await fetch(window.location.href, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'text/html',
          },
        });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextContent = doc.querySelector(
          '.dashboard--consultation-content'
        );

        if (nextContent) {
          content.innerHTML = nextContent.innerHTML;
        }
      } catch (e) {
        console.error('Failed to refresh customer charts:', e);
      } finally {
        this.isRefreshingCharts = false;
      }
    },

    async changeStatus(callId) {
      const callElement = this.dashboardApp.querySelector(
        `[data-comment-id="${callId}"]`
      );

      if (!callElement) {
        return;
      }

      const status = callElement.dataset.status;
      const acceptedStatus = status.includes('_accepted')
        ? status
        : `${status}_accepted`;
      const btnAccept = callElement.querySelector('.btn--accept');

      if (btnAccept?.disabled) {
        return;
      }

      if (btnAccept) {
        btnAccept.disabled = true;
        btnAccept.textContent = '접수중...';
      }

      try {
        const res = await fetch(
          `${frontendAjaxObject.restUrl}dcraft/v1/comments/update?user_id=${this.userId}&comment_id=${callId}&status=${acceptedStatus}`,
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'X-WP-Nonce': frontendAjaxObject.restNonce,
              Accept: 'application/json',
            },
          }
        );
        const result = await res.json();

        if (!result.ok) {
          throw new Error(result.message || 'Failed to change call status');
        }

        callElement.classList.add(acceptedStatus);
        callElement.dataset.status = acceptedStatus;
        this.messages = this.messages.map((item) =>
          Number(item.id) === Number(callId)
            ? { ...item, type: acceptedStatus }
            : item
        );
        if (!this.hasPendingMessageList(this.messages)) {
          this.pendingRealtimeNotification = false;
        }

        if (btnAccept) {
          btnAccept.textContent = '접수완료';
        }
      } catch (e) {
        console.error('Failed to change call status:', e);

        if (btnAccept) {
          btnAccept.disabled = false;
          btnAccept.textContent = '접수';
        }
      }
    },

    getMinutesDiff(dateString) {
      const requestTime = new Date(dateString); // 요청 시간
      const now = new Date(); // 현재 시간

      const diffMs = now - requestTime; // 밀리초 차이
      const diffMinutes = Math.floor(diffMs / 1000 / 60); // 분 단위로 변환

      return diffMinutes < 0 ? 0 : diffMinutes; // 음수 방지
    },

    async fetchMessages({ playSound = true } = {}) {
      const params = new URLSearchParams({
        user_id: String(this.userId),
      });
      const shopId = this.getCurrentShopId();

      if (shopId) {
        params.set('shop_id', String(shopId));
      }

      const res = await fetch(
        `${frontendAjaxObject.restUrl}dcraft/v1/comments?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-WP-Nonce': frontendAjaxObject.restNonce,
            Accept: 'application/json',
          },
        }
      );
      const list = await res.json();
      this.messageSignature = this.createMessageSignature(list);
      if (!this.hasPendingMessageList(list)) {
        this.pendingRealtimeNotification = false;
      }
      list.reverse(); // 최신순으로 정렬
      this.messages = list;
      this.dashboardApp.classList.remove('has-new-call');
      const currentIds = new Set(list.map((item) => String(item.id)));

      this.dashboardApp
        .querySelectorAll('.dashboard--message[data-comment-id]')
        .forEach((element) => {
          if (!currentIds.has(element.dataset.commentId)) {
            element.remove();
          }
        });

      if (list && list.length > 0) {
        this.dashboardApp.classList.add('has-new-call');
        // #dashboardAdd 의 가장 상단에 card 추가하기
        for (let i = 0; i < list.length; i++) {
          let title = '직원 호출';
          if (list[i].type.startsWith('cart')) title = '서비스 요청';

          let name = list[i].author;
          if (name?.startsWith('Guest-')) name = name.replace('Guest-', '');
          if (!name) name = list[i].author || '손님';

          const callId = list[i].id;
          const existingEl = this.dashboardApp.querySelector(
            `[data-comment-id="${callId}"]`
          );

          let actionButton = '';
          if (list[i].type && list[i].type.includes('accepted')) {
            actionButton = `<button class="btn btn--accept" disabled>접수완료</button>`;
          } else {
            actionButton = `<button class="btn btn--accept" type="button" @click="changeStatus(${callId})">접수</button>`;
          }

          const minutesAgo = this.getMinutesDiff(list[i].date);

          const contentHtml = list[i].content
            ? list[i].content.replace(/\n/g, '<br>')
            : '';

          const html = `
            <div class="dashboard--message ${list[i].type}" data-comment-id="${callId}" data-status="${list[i].type}" data-comment-date="${list[i].date}">
              <header class="dashboard--header">
                <h3 class="dashboard--title">${title} <span class="nickname">${name}</span></h3>
                ${actionButton}
              </header>
              <div class="dashboard--content">
                <div class="dashboard--content-message">${contentHtml}</div>
              </div>
              <div class="dashboard--time">
                <div class="time-info">
                  <time>
                    <span>요청시간</span>
                    ${new Date(list[i].date).toLocaleTimeString('ko-KR', {
                      hour12: false,
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </time>
                  <ins><span class="font-clock elapsed-minutes">${minutesAgo}</span> 분 전</ins>
                </div> 
              </div>
            </div>
          `;

          if (existingEl) {
            // 기존 요소 업데이트
            existingEl.outerHTML = html;
          } else {
            // 새로운 요소 추가
            this.dashboardApp.insertAdjacentHTML('afterbegin', html);
            if (playSound) {
              this.playNotificationSound();
            }
          }
        }
      }
      if (list.length) this.lastId = list[list.length - 1].id;
    },

    updateMessageAges() {
      if (!this.dashboardApp) {
        return;
      }

      this.dashboardApp
        .querySelectorAll('.dashboard--message[data-comment-date]')
        .forEach((element) => {
          const target = element.querySelector('.elapsed-minutes');
          if (!target) {
            return;
          }

          target.textContent = this.getMinutesDiff(element.dataset.commentDate);
        });
    },

    async fetchMessageChanges({ playSound = true } = {}) {
      if (this.dashboardView !== 'notifications' || document.hidden) {
        return;
      }

      const params = new URLSearchParams({
        user_id: String(this.userId),
        signature: this.messageSignature,
      });
      const shopId = this.getCurrentShopId();

      if (shopId) {
        params.set('shop_id', String(shopId));
      }

      const res = await fetch(
        `${frontendAjaxObject.restUrl}dcraft/v1/comments/changes?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-WP-Nonce': frontendAjaxObject.restNonce,
            Accept: 'application/json',
          },
        }
      );
      const result = await res.json();

      if (result.changed) {
        await this.fetchMessages({ playSound });
        return;
      }

      this.messageSignature = result.signature || this.messageSignature;
    },

    createMessageSignature(list) {
      if (!Array.isArray(list)) {
        return '';
      }

      return list
        .map((item) => `${item.id}:${item.type}:${item.date}`)
        .join('|');
    },
  };
}
